import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useParams,
} from "react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { useAuth } from "../contexts/Auth/useAuth";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { ActiveHospitalProvider } from "../contexts/ActiveHospital";
import { ActiveConvenioProvider } from "../contexts/ActiveConvenio";
import { ReportModalProvider } from "../contexts/ReportModal";
import { useActiveHospital } from "../contexts/ActiveHospital/useActiveHospital";
import { AppProvider } from "../contexts";

import Aside from "../components/Aside";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { ModalComponent } from "../components/Modal";
import { PrimaryButton } from "../components/Buttons";

import styled from "styled-components";
import { theme } from "../styles/theme";
import { ROLE_CONVENIO, ROLE_DOCTOR, ROLE_HOSPITALS, ROLE_PATIENT } from "../types/role";

// =============================================================================
// LAZY LOADED PAGES - Code Splitting for better performance
// =============================================================================

// Public pages
const Home = lazy(() => import("../pages"));
const Login = lazy(() => import("../pages/login"));
const ForgotPassword = lazy(() => import("../pages/Users/ForgotPassword/ForgotPassword"));
const HospitalarLogin = lazy(() => import("../pages/login/hospitalar/hospitalar"));
const ConvenioLogin = lazy(() => import("../pages/login/convenio/convenio"));
const MedicoLogin = lazy(() => import("../pages/login/medico/medico"));
const PacienteLogin = lazy(() => import("../pages/login/paciente/paciente/paciente"));
const SenneLogin = lazy(() => import("../pages/login/Senne/index"));
const IdentifierSearch = lazy(() => import("../pages/login/paciente/IdentifierSearch/index"));
const ValidateLoginPatient = lazy(() => import("../pages/login/paciente/loginSMS/loginSMS"));
const SelectBirthdate = lazy(() => import("../pages/login/paciente/SelectBirthdate/index"));

// Registration pages
const Register = lazy(() => import("../pages/Register/index"));
const RegisterPatientLookup = lazy(() => import("../pages/Register/patient/PatientLookup"));
const RegisterPatient = lazy(() => import("../pages/Register/patient/PatientRegister/patientRegister"));
const RegisterPatientValidationChoice = lazy(() => import("../pages/Register/patient/ValidationChoice"));
const RegisterDoctor = lazy(() => import("../pages/Register/doctor/DoctorStepOne/doctorStepOne"));
const RegisterHospital = lazy(() => import("../pages/Register/hospital/HospitalStepOne/hospitalStepOne"));
const RegisterConvenio = lazy(() => import("../pages/Register/convenio/ConvenioStepOne/convenioStepOne"));
const ValidatePatient = lazy(() => import("../pages/Register/patient/PatientValidate/patientValidate"));
const InsertEmail = lazy(() => import("../pages/Register/patient/InsertEmail"));
const InsertCellphone = lazy(() => import("../pages/Register/patient/InsertCellphone"));
const ValidateEmailOrCellphone = lazy(() => import("../pages/Register/patient/ValidateEmailOrCellphone"));
const ValidateSMSPage = lazy(() => import("../pages/ValidateSMS"));
const CadastroAprovado = lazy(() => import("../pages/Register/patient/CadastroAprovado/cadastroAprovado"));
const CadastroSolicitadoMed = lazy(() => import("../pages/Register/doctor/CadastroSolicitadoMed/cadastroSolicitadoMed"));
const CadastroSolicitadoUserHospital = lazy(() => import("../pages/Register/hospital/CadastroSolicitadoUserHospital/cadastroSolicitadoUserHospital"));
const ResetPassword = lazy(() => import("../pages/Users/ResetPassword/ResetPassword"));

// Authenticated pages
const Dashboard = lazy(() => import("../pages/Dashboard"));
const Exams = lazy(() => import("../pages/Exams"));
const DetailExam = lazy(() => import("../pages/Exams/Details"));
const Users = lazy(() => import("../pages/Users"));
const AddUser = lazy(() => import("../pages/Users/AddUser/AddUser"));
const Profile = lazy(() => import("../pages/Users/Profile/Profile"));
const EditProfile = lazy(() => import("../pages/Users/EditProfile/EditProfile"));
const Report = lazy(() => import("../pages/Report").then(m => ({ default: m.Report })));
const Occurrence = lazy(() => import("../pages/Occurrence"));
const GridAttendance = lazy(() => import("../components/GridAttendance"));

// Internal Doctors pages
const InternalDoctorsForm = lazy(() => import("../pages/InternalDoctors"));
const InternalDoctorsTemplates = lazy(() => import("../pages/InternalDoctorsTemplates"));
const InternalDoctorDocuments = lazy(() => import("../pages/InternalDoctorsDocuments/InternalDoctorsDocuments"));
const InternalDoctorsManagement = lazy(() => import("../pages/InternalDoctorsManagement"));

// Addition Request pages
const AdditionRequest = lazy(() => import("../pages/AdditionRequest").then(m => ({ default: m.AdditionRequest })));
const CarlaoAnalytics = lazy(() => import("../pages/CarlaoAnalytics"));
const SearchAppointment = lazy(() => import("../pages/AdditionRequest/SearchAppointment").then(m => ({ default: m.SearchAppointment })));
const SelectTypeRequest = lazy(() => import("../pages/AdditionRequest/SelectTypeRequest").then(m => ({ default: m.SelectTypeRequest })));
const SelectAppointment = lazy(() => import("../pages/AdditionRequest/SelectAppointment").then(m => ({ default: m.SelectAppointment })));
const CarlaoWizardPage = lazy(() => import("../pages/CarlaoWizard"));
const AdditionManagement = lazy(() => import("../pages/AdditionManagement").then(m => ({ default: m.AdditionManagement })));

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const ModalContainer = styled.div`
  padding: 0rem 3rem;
  h1 {
    text-align: center;
    margin-bottom: 2.5rem;
    font-size: ${theme.fontSize["4xl"]};
    font-weight: 600;
  }
  p {
    margin-bottom: 2.5rem;
    font-size: ${theme.fontSize["2xl"]};
  }
`;

// =============================================================================
// AUTH GUARDS
// =============================================================================

function LegacyWizardRedirect() {
  const { sessionId } = useParams<{ sessionId?: string }>();
  return (
    <Navigate
      to={
        sessionId
          ? `/carlao/novo-acrescimo/${sessionId}`
          : "/carlao/novo-acrescimo"
      }
      replace
    />
  );
}

interface RequireAuthProps {
  children: ReactNode;
}

function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated: isLoggedIn, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <RouteLoader message="Verificando autenticação..." />;
  }

  if (!isLoggedIn) {
    // Encode the full path+search so it survives the round-trip through the URL
    const redirectParam = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login/administrador?redirect=${redirectParam}`} replace />;
  }

  return <>{children}</>;
}

interface ValidateAuthProps {
  children: ReactNode;
  roles?: number[];
  permissions?: number[];
  hospitalNeedsToBeActive?: boolean;
}

function ValidateAuth({
  children,
  roles = [],
  permissions = [],
  hospitalNeedsToBeActive = false,
}: ValidateAuthProps) {
  const { isLoading, user } = useAuth();
  const activeHospital = useActiveHospital();

  if (isLoading) {
    return <RouteLoader message="Carregando permissões..." />;
  }

  // Check if user has the required role
  const hasRole = roles.length === 0 || roles.includes(user?.role_id!);

  // Determine redirect path based on user role
  const redirectPath =
    user?.role_id === 5
      ? "/formulario-interno"
      : user?.role_id === ROLE_PATIENT || user?.role_id === ROLE_DOCTOR
        ? "/resultados-exames"
        : "/dashboard";

  // Required role missing — block access
  if (!hasRole && user?.role_id !== ROLE_OPERATION) {
    return <Navigate to={redirectPath} replace />;
  }

  // Permission and hospital checks apply to ROLE_HOSPITALS and ROLE_CONVENIO
  // Other roles (admin/ops) skip these checks
  if (user?.role_id === ROLE_HOSPITALS || user?.role_id === ROLE_CONVENIO) {
    // Extract user permissions

    const userPermissions = user?.permissoes?.map((item: unknown) => {
      if (typeof item === 'number') return item;
      if (typeof item === 'object' && item !== null && 'id' in item) {
        return Number((item as { id: number | string }).id);
      }
      return 0;
    }) || [];

    // Check if user has the required permissions
    const hasPermission =
      permissions.length === 0 ||
      permissions.some((permission) => userPermissions.includes(permission));

    // Block hospital without permission
    if (!hasPermission) {

      return <Navigate to={redirectPath} replace />;
    }

    // isProcedenciaActive check only applies to ROLE_HOSPITALS
    if (user?.role_id === ROLE_HOSPITALS) {
      // Check if hospital is inactive
      const inactiveHospital = activeHospital?.isProcedenciaActive === false;
      const hospitalBlocked = inactiveHospital && hospitalNeedsToBeActive;

      // Block inactive hospital when required
      if (hospitalBlocked) {

        return <Navigate to={redirectPath} replace />;
      }
    }
  }


  // All checks passed — grant access
  return <>{children}</>;
}

function ConvenioRouteGuard({ children }: { children: ReactNode }) {
  const { enabled, loading } = useFeatureFlag("habilitar_convenio");

  if (loading) return <RouteLoader message="Verificando acesso..." />;
  if (!enabled) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <Outlet />
      </AppProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

function PublicLayout() {
  return (
    <Suspense fallback={<RouteLoader />}>
      <Outlet />
    </Suspense>
  );
}

const PlaceholderDateTime = () => {
  const [date, setDate] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setDate(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  const formattedTime = new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);

  const formattedWeek = new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
  }).format(date);

  const formattedDay = new Intl.DateTimeFormat("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);

  return (
    <>
      <Datetime>
        <span className="time">{formattedTime}</span>
        <span className="date">
          {formattedWeek.replace("", "")},
        </span>
        <span className="date">
          {formattedDay}
        </span>
      </Datetime>
    </>
  );
};

function AuthenticatedLayout() {
  const { isExpiredTokenModalOpen, setIsExpiredTokenModalOpen, logout } = useAuth();

  const closeModal = () => {
    logout();
    setIsExpiredTokenModalOpen(false);
  };

  return (
    <RequireAuth>
      <ActiveHospitalProvider>
        <ActiveConvenioProvider>
          <ReportModalProvider>
            <Aside customPlaceholder={
              <PlaceholderDateTime />
            }>
              <Suspense fallback={<RouteLoader message="Carregando página..." />}>
                <Outlet />
              </Suspense>
              {isExpiredTokenModalOpen && (
                <ModalComponent closeModal={closeModal}>
                  <ModalContainer>
                    <h1>Atenção</h1>
                    <p>Sua sessão expirou. Por favor, faça login novamente.</p>
                    <PrimaryButton onClick={closeModal}>OK</PrimaryButton>
                  </ModalContainer>
                </ModalComponent>
              )}
            </Aside>
          </ReportModalProvider>
        </ActiveConvenioProvider>
      </ActiveHospitalProvider>
    </RequireAuth>
  );
}

// =============================================================================
// ROUTE CONFIGURATION (imported from constants for reuse)
// =============================================================================

import {
  ROLE_INTERNAL_DOCTOR,
  ROLE_OPERATION,
} from "../types/role";
import {
  PERMISSION_ADMINISTRATOR,
  PERMISSION_REPORTS,
  PERMISSION_RESULT,
  PERMISSION_SCHEDULING,
} from "../types/permissions";
import { Datetime } from "../components/Aside/style";
import RouteLoader from "../components/RouteLoader";

// =============================================================================
// ROUTER DEFINITION
// =============================================================================

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      // Public routes
      {
        element: <PublicLayout />,
        children: [
          { path: "/", element: <Home /> },
          { path: "/login", element: <Login /> },
          { path: "/nova-senha", element: <ForgotPassword /> },
          { path: "/login/paciente", element: <IdentifierSearch /> },
          { path: "/login/paciente/birthdate", element: <SelectBirthdate /> },
          { path: "/login/protocol", element: <PacienteLogin /> },
          { path: "/login/administrador", element: <SenneLogin /> },
          { path: "/login/hospital", element: <HospitalarLogin /> },
          {
            path: "/login/convenio",
            element: (
              <ConvenioRouteGuard>
                <ConvenioLogin />
              </ConvenioRouteGuard>
            ),
          },
          { path: "/login/medico", element: <MedicoLogin /> },
          { path: "/login/paciente/login-sms", element: <ValidateLoginPatient /> },
          { path: "/cadastro", element: <Register /> },
          { path: "/reset-password", element: <ResetPassword /> },
          { path: "/cadastro/paciente", element: <RegisterPatientLookup /> },
          { path: "/cadastro/paciente/dados", element: <RegisterPatient /> },
          { path: "/cadastro/paciente/validacao", element: <RegisterPatientValidationChoice /> },
          { path: "/cadastro/paciente/email", element: <InsertEmail /> },
          { path: "/cadastro/paciente/cellphone", element: <InsertCellphone /> },
          { path: "/validar/paciente", element: <ValidatePatient /> },
          { path: "/validar/paciente/email", element: <ValidateEmailOrCellphone type="email" /> },
          { path: "/validar/paciente/cellphone", element: <ValidateEmailOrCellphone type="cellphone" /> },
          { path: "/validar/paciente/both", element: <ValidateEmailOrCellphone type="both" /> },
          { path: "/validar/numero", element: <ValidateSMSPage /> },
          { path: "/cadastro/medico", element: <RegisterDoctor /> },
          { path: "/cadastro/hospital", element: <RegisterHospital /> },
          {
            path: "/cadastro/convenio",
            element: <Navigate to="/cadastro" replace />,
          },
          { path: "/cadastro-aprovado", element: <CadastroAprovado /> },
          { path: "/cadastro-solicitado-medico", element: <CadastroSolicitadoMed /> },
          { path: "/cadastro-solicitado", element: <CadastroSolicitadoUserHospital /> },
        ],
      },

      // Authenticated routes - Internal Doctors
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            path: "/formulario-interno",
            element: (
              <ValidateAuth roles={[ROLE_INTERNAL_DOCTOR]}>
                <InternalDoctorsForm />
              </ValidateAuth>
            ),
          },
          {
            path: "/doctors-templates",
            element: (
              <ValidateAuth roles={[ROLE_INTERNAL_DOCTOR]}>
                <InternalDoctorsTemplates />
              </ValidateAuth>
            ),
          },
          {
            path: "/documentos",
            element: (
              <ValidateAuth roles={[ROLE_INTERNAL_DOCTOR]}>
                <InternalDoctorDocuments />
              </ValidateAuth>
            ),
          },
        ],
      },

      // Authenticated routes - Operation
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            path: "/acrescimo",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <AdditionManagement />
              </ValidateAuth>
            ),
          },
          {
            path: "/doctors-templates/:user_id",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION]}>
                <InternalDoctorsManagement />
              </ValidateAuth>
            ),
          },
          {
            path: "/resultados-exames/:uuid/:id/addition",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <AdditionRequest />
              </ValidateAuth>
            ),
          },
          {
            path: "/resultados-exames/addition/search",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <SearchAppointment />
              </ValidateAuth>
            ),
          },
          {
            path: "/resultados-exames/addition/select-type",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <SelectTypeRequest />
              </ValidateAuth>
            ),
          },
          {
            path: "/select-appointment",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR]}>
                <SelectAppointment />
              </ValidateAuth>
            ),
          },
          {
            path: "/occurrence",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION]}>
                <Occurrence />
              </ValidateAuth>
            ),
          },
        ],
      },

      // Authenticated routes - Mixed roles
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            path: "/resultados",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR, PERMISSION_RESULT]}
              >
                <GridAttendance pageSize={10} />
              </ValidateAuth>
            ),
          },
          {
            path: "/relatorio/:report_id",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS]}
                permissions={[PERMISSION_ADMINISTRATOR, PERMISSION_REPORTS]}
                hospitalNeedsToBeActive
              >
                <Report />
              </ValidateAuth>
            ),
          },
          {
            path: "/resultados-exames",
            element: (
              <ValidateAuth roles={[ROLE_PATIENT, ROLE_DOCTOR]}>
                <Exams />
              </ValidateAuth>
            ),
          },
          {
            path: "/dashboard",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR, PERMISSION_RESULT, PERMISSION_SCHEDULING]}
              >
                <Dashboard />
              </ValidateAuth>
            ),
          },
          {
            path: "/resultados-exames/:uuid",
            element: (
              <ValidateAuth
                roles={[ROLE_PATIENT, ROLE_DOCTOR, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR, PERMISSION_RESULT]}
              >
                <Exams />
              </ValidateAuth>
            ),
          },
          {
            path: "/resultados-exames/:uuid/:id",
            element: (
              <ValidateAuth
                roles={[ROLE_PATIENT, ROLE_DOCTOR, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR, PERMISSION_RESULT]}
              >
                <DetailExam />
              </ValidateAuth>
            ),
          },
        ],
      },

      // Authenticated routes - User management
      {
        element: <AuthenticatedLayout />,
        children: [
          {
            path: "/users",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <Users />
              </ValidateAuth>
            ),
          },
          {
            path: "/carlao/novo-acrescimo/:sessionId?",
            element: (
              <ValidateAuth roles={[ROLE_OPERATION, ROLE_HOSPITALS]}>
                <CarlaoWizardPage />
              </ValidateAuth>
            ),
          },
          // Redirect legacy bookmarks for /carlao/wizard -> /carlao/novo-acrescimo
          {
            path: "/carlao/wizard",
            element: <Navigate to="/carlao/novo-acrescimo" replace />,
          },
          {
            path: "/carlao/wizard/:sessionId",
            element: <LegacyWizardRedirect />,
          },
          {
            path: "/analises-carlao",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION]}
                permissions={[PERMISSION_ADMINISTRATOR]}
              >
                <CarlaoAnalytics />
              </ValidateAuth>
            ),
          },
          {
            path: "/users/add",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <AddUser />
              </ValidateAuth>
            ),
          },
          {
            path: "/users/:id",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <Profile />
              </ValidateAuth>
            ),
          },
          {
            path: "/users/:id/editar",
            element: (
              <ValidateAuth
                roles={[ROLE_OPERATION, ROLE_HOSPITALS, ROLE_CONVENIO]}
                permissions={[PERMISSION_ADMINISTRATOR]}
                hospitalNeedsToBeActive
              >
                <EditProfile />
              </ValidateAuth>
            ),
          },
        ],
      },

      // Catch-all 404.
      //
      // Rendered as a plain `element`, `useRouteError()` returns undefined,
      // `isRouteErrorResponse` is false, and `getErrorInfo` falls to its
      // default branch - so a mistyped URL said "Erro inesperado / Ocorreu
      // um erro inesperado", telling the user the app had crashed, while
      // the 404 copy already in ErrorBoundary never rendered. It also fired
      // a level:"fatal" Slack alert carrying an empty error detail, so part
      // of that channel was mistyped URLs rather than crashes.
      //
      // Throwing a 404 Response from a loader is what makes
      // `isRouteErrorResponse` true, so the existing 404 branch is reached.
      {
        path: "*",
        loader: () => {
          throw new Response(null, { status: 404, statusText: "Not Found" });
        },
        errorElement: <ErrorBoundary />,
      },
    ]
  },
]);

// Export default for backward compatibility
export default function Router() {
  return null; // This component is no longer used directly
}
